import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCall, screenSupported } from './CallProvider';
import { Avatar } from '../../components/Avatar';
import { CallMini } from './CallMini';
import { DeviceMenu } from './DeviceMenu';
import { ScreenMenu, ScreenMenuItem } from './ScreenMenu';
import {
  callTitle,
  mainSpeaker,
  needsSuccessor,
  screenShareTargets,
  shareHint,
  stageMode,
  type Corner,
  showsMessageToast,
  timerText,
  windowButtons,
} from './callMath';
import { BoardControl, BoardPanel, BoardSaveButton, BoardStage, useBoard } from './Board';
import { Grid } from './Grid';
import { Strip } from './Strip';
import { InviteDialog, MAX_IN_CALL } from './InviteDialog';
import { TimerDialog } from './TimerDialog';
import { CallSide } from './CallSide';
import { ScreenDrawLayer, useScreenDraw } from './ScreenDraw';
import { PanelHead } from '../../nav/PanelHead';
import { CallChat } from './CallChat';
import { ParticipantTile } from './Tile';
import { REMOTE_MEDIA_CLASS } from './devices';
import { clock } from '../callCard';
import { plural } from '../../lib/kb';
import { useIsMobile } from '../../lib/media';
import { useNavLock } from '../../nav/lock';
import {
  IconCamera,
  IconCameraOff,
  IconEraser,
  IconExpand,
  IconGear,
  IconGroups,
  IconHand,
  IconChat,
  IconCollapse,
  IconArrowLeft,
  IconBoard,
  IconMore,
  IconTimer,
  IconElapsed,
  IconMic,
  IconMicOff,
  IconMinimize,
  IconPresenter,
  IconPhone,
  IconPhoneOff,
  IconPlus,
  IconScreen,
  IconSignal,
} from '../../components/icons';

/*
 * Экран звонка. Живёт над стопкой экранов: стопка сбрасывается при смене
 * раздела, а звонок обязан пережить любые переходы. Полный режим — слой на
 * весь экран; свёрнутый рисует CallMini. Кнопка сброса всегда красная и на
 * одном месте — её ищут в спешке.
 */

/** Порядок обхода углов для своей миниатюры: по часовой стрелке. */
const NEXT_CORNER: Record<Corner, Corner> = { br: 'bl', bl: 'tl', tl: 'tr', tr: 'br' };

/** Сколько лиц помещается в карточку дозвона; остальные считаются числом. */
const RING_FACES = 4;

export function CallLayer() {
  const call = useCall();
  if (call.phase === 'idle') return null;
  return (
    <>
      {/* Звук участников: отдельные элементы над слоем, а не в плитках.
          Плитка живёт, пока её видно, — свёрнутый звонок и раскладка
          «докладчик» иначе оставляли бы людей без голоса. */}
      {call.participants
        .filter((p) => !p.isMe)
        .map((p) => (
          <RemoteAudio key={p.id} userId={p.id} />
        ))}
      {call.view === 'mini' ? <CallMini /> : <FullCall />}
    </>
  );
}

/** Голос одного участника. Отдельный компонент ради отцепления при уходе. */
function RemoteAudio({ userId }: { userId: string }) {
  const { attach, trackKeys, speakerId, gains } = useCall();
  const ref = useRef<HTMLAudioElement>(null);
  const key = trackKeys[`${userId}:audio`];
  const gain = gains[userId] ?? 1;

  // Автомикшер: молчащие приглушены. На iPhone громкость элемента страница
  // менять не может — там звучит как раньше.
  useEffect(() => {
    if (ref.current) ref.current.volume = gain;
  }, [gain, key]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return attach(el, userId, 'audio');
  }, [attach, userId, key]);

  // выбранный динамик ставим здесь: этот элемент и есть звук собеседника, и
  // он может появиться позже выбора — вошедшего последним иначе не слышно
  useEffect(() => {
    const el = ref.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!el || !speakerId || !el.setSinkId) return;
    void el.setSinkId(speakerId).catch(() => {});
  }, [speakerId]);

  return <audio ref={ref} className={REMOTE_MEDIA_CLASS} autoPlay hidden />;
}

function FullCall() {
  const call = useCall();
  const board = useBoard(call);
  /* Пометки поверх демонстрации живут, только пока идёт показ: ни листа, ни
     файла за ними нет (решение Ильи 16.09.2026). */
  const screenDraw = useScreenDraw({ enabled: !!call.screenOn || call.participants.some((p) => p.screenOn) });
  const mobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const [fresh, setFresh] = useState(true);
  const [menu, setMenu] = useState(false);
  const [screenMenu, setScreenMenu] = useState<HTMLElement | null>(null);
  /** Меню выбора, кому передать роль ведущего. */
  const [hostMenu, setHostMenu] = useState<HTMLElement | null>(null);
  const [reactionMenu, setReactionMenu] = useState<HTMLElement | null>(null);
  /** Окно выбора преемника: открывается, когда звонок покидает ведущий. */
  const [successor, setSuccessor] = useState(false);
  /** Пункты доски для меню «ещё» на телефоне: их отдаёт BoardControl. */
  const [boardItems, setBoardItems] = useState<ScreenMenuItem[]>([]);
  const [moreMenu, setMoreMenu] = useState<HTMLElement | null>(null);
  /** Окно отсчёта: заводит и снимает его только ведущий. */
  const [timerOpen, setTimerOpen] = useState(false);
  /** Переписка звонка открыта: в вебе вкладкой правой панели, на телефоне — экраном стопки. */
  const [chatOpen, setChatOpen] = useState(false);
  /** Угол своей миниатюры в разговоре вдвоём: нажатие переносит её дальше. */
  const [selfCorner, setSelfCorner] = useState<Corner>('br');
  /**
   * Боковая панель десктопа раскрыта: она же задаёт раскладку сцены. Звонок
   * начинается со свёрнутой панели (решение Ильи 15.09.2026): первым делом
   * человек смотрит на людей, а не на список участников.
   */
  const [panelOpen, setPanelOpen] = useState(false);
  /*
   * Панель уезжает с той же анимацией, с какой приезжала (замечание Ильи
   * 15.09.2026). Для этого её нельзя убрать из разметки сразу: держим ещё
   * четверть секунды с классом ухода, а потом снимаем.
   */
  const [sideLeaving, setSideLeaving] = useState(false);
  /** Карточка нового сообщения поверх содержимого: живёт четыре секунды. */
  const [toast, setToast] = useState<{ name: string; text: string } | null>(null);
  const [endMenu, setEndMenu] = useState<HTMLElement | null>(null);
  // кого администратор выбрал в полосе участников и чем открыл меню
  const [picked, setPicked] = useState<{ id: string; name: string; isMe: boolean; anchor: HTMLElement } | null>(null);
  /* Закреплённый участник — личный выбор смотрящего, на сервер не уходит:
     каждый смотрит своё (макет Ильи 11.09.2026, «Мария · закреплена»). */
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [invite, setInvite] = useState(false);
  /** Кто был крупно в прошлый раз: пока все молчат, плитка не прыгает. */
  const [sticky, setSticky] = useState<string | null>(null);

  // на телефоне слой заперт от прокрутки страницы, как экраны стопки
  useNavLock(mobile);

  // Въезд на телефоне: смонтировались за краем и сняли класс после
  // синхронного reflow — как в StackHost, не через rAF.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    void el.offsetWidth;
    setFresh(false);
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // на сцене только те, с кем разговор идёт или вот-вот начнётся: ушедшие и
  // отказавшиеся плиток не занимают. Когда таких не осталось (звонок отклонён,
  // экран живёт ещё четыре секунды), показываем тех, с кем он был, — иначе
  // человек смотрит в чёрный прямоугольник без единого имени.
  const shown = useMemo(() => {
    const live = call.participants.filter((p) => p.status === 'active' || p.status === 'ringing');
    return live.length ? live : call.participants;
  }, [call.participants]);
  const others = shown.filter((p) => !p.isMe);
  const speakerId = mainSpeaker(shown, sticky);
  // Закрепление перебивает говорящего: человек попросил смотреть на одного.
  const pinned = pinnedId && shown.some((p) => p.id === pinnedId) ? pinnedId : null;
  const mainId = pinned ?? speakerId;
  useEffect(() => setSticky(speakerId), [speakerId]);

  const sharer = shown.find((p) => p.screenOn) ?? null;
  const viewer = !!sharer && !sharer.isMe;
  // Сцена с содержимым (3.34): доска и показ экрана — один и тот же слот, доска в приоритете —
  // открытие листа гасит показ (см. openBlank/openPage в useBoard).
  const boardShown = !!board.sheet;
  const contentActive = !!sharer || boardShown;
  const activeCount = shown.filter((p) => p.status === 'active').length;
  const caller = shown.find((p) => p.id === call.adminId) ?? others[0] ?? null;

  const status =
    call.error ||
    (call.phase === 'incoming'
      ? activeCount > 1
        ? `в звонке ${plural(activeCount, ['человек', 'человека', 'человек'])}`
        : call.kind === 'video'
          ? 'Видеозвонок'
          : 'Звонит'
      : call.phase === 'outgoing'
        ? 'Вызываем…'
        : call.reconnecting
          ? 'Связь прервалась, восстанавливаем…'
          : call.quality === 1
            ? 'Слабая связь'
            : '');

  // Новое сообщение, пока переписка закрыта: обычно хватает счётчика, но во
  // время своего показа экрана или доски человек смотрит в содержимое —
  // тогда сообщение всплывает карточкой на четыре секунды (спека 8).
  const lastMessage = call.messages[call.messages.length - 1];
  const lastMessageId = lastMessage?.id;
  useEffect(() => {
    if (!lastMessage || lastMessage.author.id === call.me) return;
    if (!showsMessageToast({ chatOpen, screenOn: call.screenOn, boardOpen: boardShown, surface: call.shareSurface })) {
      return;
    }
    setToast({ name: lastMessage.author.name, text: lastMessage.text || 'Вложение' });
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
    // следим за появлением нового сообщения, а не за каждым полем состояния
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessageId]);

  // время вышло: второй счётчик идёт вверх и красится в красный
  const overtime = !!call.timerEndsAt && new Date(call.timerEndsAt).getTime() < Date.now();

  const title =
    call.phase === 'incoming'
      ? `${caller?.name ?? 'Кто-то'} зовёт в звонок`
      : callTitle(others.map((p) => p.name));

  // полоса нужна там, где сцену занял один: показ экрана или доска (по кнопке) и
  // раскладка «докладчик» (всегда, иначе остальных вообще не видно)
  const stripSide = mobile ? 'bottom' : 'right';
  /*
   * Раскладку десктопа задаёт боковая панель (решение Ильи 11.09.2026):
   * открыта — слева крупно говорящий, справа список всех участников или
   * переписка; свёрнута — равные плитки. Отдельной кнопки раскладки на
   * десктопе нет. Само правило — в callMath.stageMode, под тестами.
   */
  // Панель открыта только в разговоре: во время дозвона показывать список
  // участников и переписку не на чем, а сцена из-за неё сжималась (замечание
  // Ильи 15.09.2026).
  const sideOpen = !mobile && call.phase === 'active' && (panelOpen || chatOpen);
  const sideShown = sideOpen || sideLeaving;
  const closeSide = () => {
    setChatOpen(false);
    setPanelOpen(false);
    setSideLeaving(true);
    window.setTimeout(() => setSideLeaving(false), 240);
  };
  const { layout, duo, strip: stripOn } = stageMode({
    mobile,
    sideOpen,
    contentActive,
    people: shown.length,
    mobileLayout: call.layout,
    stripShown: call.stripShown,
  });
  // В полосе телефона все, кроме занявшего сцену; в панели десктопа — все,
  // включая крупного и меня: это список участников, а не «остальные».
  const stripPeople = contentActive ? shown : shown.filter((p) => p.id !== mainId);
  /*
   * Окошко в углу. Вдвоём на телефоне это своё лицо, а во время показа экрана
   * и доски — тот, кто сейчас говорит (решение Ильи 16.09.2026): на маленьком
   * экране полоса миниатюр отнимает у содержимого слишком много, а знать, кто
   * говорит, всё равно нужно. Переносится нажатием по углам.
   */
  const cornerTile = duo
    ? shown.find((p) => p.isMe) ?? null
    : mobile && contentActive
      ? shown.find((p) => p.id === mainId) ?? null
      : null;

  // Право показа у держателя, а ведущий показывать может всегда (спека
  // 2026-09-25-guest-screen-share): подсказка называет держателя.
  const holderId = call.screenShareHolderId ?? call.boardHostId ?? '';
  const presenterName = shown.find((p) => p.id === holderId)?.name ?? '';
  const hint = shareHint({
    supported: screenSupported,
    active: call.phase === 'active',
    iAmPresenter: holderId === call.me || (!call.isGuest && call.boardHostId === call.me),
    peerScreen: viewer,
    screenOn: call.screenOn,
    peerName: presenterName || sharer?.name || '',
  });
  /* Роль в звонке одна — ведущий (решение Ильи 15.09.2026): приглашать,
     удалять, завершать для всех, показывать экран, рисовать и заводить
     отсчёт. Передача роли уносит весь набор разом. */
  const iAmHost = !call.isGuest && call.boardHostId === call.me;

  // всё про показ экрана — одним списком; пункты зависят от права и состояния
  const screenItems: ScreenMenuItem[] = [];
  if (call.screenOn) {
    screenItems.push({ label: 'Остановить показ', onPick: () => call.stopScreen(), danger: true });
  } else if (call.canShare) {
    // Открытый лист доски сам не гаснет от показа (маршрута под это нет) — клиент
    // закрывает его тем же правом, что и кнопка «Закрыть», перед стартом показа.
    // Один пункт вместо двух (решение Ильи 15.09.2026): что именно показать —
    // окно, вкладку или весь экран — человек и так выбирает в окне браузера,
    // и спрашивать об этом дважды незачем.
    screenItems.push({
      label: 'Начать демонстрацию экрана',
      onPick: () => void board.closeIfSharing().then(() => call.startScreen('monitor')),
    });
  }
  // Право показа выдаёт ведущий — любому в разговоре, включая гостя; тем,
  // чей браузер экран не показывает, не предлагается (спека
  // 2026-09-25-guest-screen-share).
  if (iAmHost && call.phase === 'active') {
    const holder = holderId !== call.me ? shown.find((p) => p.id === holderId) : undefined;
    if (holder) screenItems.push({ label: `Забрать показ у ${holder.name}`, onPick: () => void call.setScreenShare(null) });
    for (const p of screenShareTargets(others, call.me, holderId)) {
      screenItems.push({ label: `Дать показ: ${p.name}`, onPick: () => void call.setScreenShare(p.id) });
    }
  }
  /* Передача роли — своя кнопка в плашке, а не пункт в меню показа экрана
     (решение Ильи 15.09.2026): это про управление звонком, а не про
     демонстрацию. Роль уходит целиком, и забрать её обратно прежний ведущий
     не может — передаёт тот, у кого она сейчас. Гостю роль не достаётся:
     сервер такого не принимает. */
  const hostItems: ScreenMenuItem[] = iAmHost
    ? others
        .filter((p) => p.status === 'active' && p.id !== call.boardHostId && !p.id.startsWith('guest:'))
        .map((p) => ({ label: p.name, onPick: () => void call.setHost(p.id) }))
    : [];
  // Отсчёт заводит ведущий — своей кнопкой в плашке (решение Ильи 11.09.2026:
  // в меню показа экрана ему не место). У разговора встречи кнопки нет: там
  // отсчёт идёт по календарю (спека 3.3).
  const canSetTimer =
    !call.isGuest && !call.meeting && call.boardHostId === call.me && call.phase === 'active';
  const screenTitle = screenItems.length ? (call.screenOn ? 'Показ экрана' : 'Экран') : hint || 'Экран';

  // Администратор уходит из комнаты один или гасит её для всех: комната живёт
  // без него, поэтому «выйти» и «завершить» это разные действия, а не одно.
  /* Реакции: набор закрытый, палитры эмодзи нет — в рабочем разговоре она
     превращается в шум (решение Ильи). Рука живёт своей кнопкой: она
     висит, пока её не опустят, и к вспыхивающим реакциям не относится. */
  const reactionItems: ScreenMenuItem[] = [
    { label: call.hand ? 'Опустить руку' : 'Поднять руку', onPick: call.toggleHand },
    { label: 'Палец вверх', onPick: () => call.sendReaction('up') },
    { label: 'Палец вниз', onPick: () => call.sendReaction('down') },
    { label: 'Хлопок', onPick: () => call.sendReaction('clap') },
  ];

  /* Сотрудники, которым можно передать роль: все, кроме меня и гостей. */
  const staffLeft = shown.filter((p) => !p.isMe && !p.id.startsWith('guest:'));
  const leave = () => {
    if (needsSuccessor({ isHost: iAmHost, staffLeft: staffLeft.length })) setSuccessor(true);
    else call.hangup();
  };

  const endItems: ScreenMenuItem[] = [
    { label: 'Выйти', onPick: leave },
    { label: 'Завершить для всех', danger: true, onPick: () => void call.closeRoom() },
  ];

  // при заполненном звонке приглашать некого — прятать кнопку, а не звать сервер за отказом
  const inCall = call.phase === 'active' || call.phase === 'outgoing';
  const canInvite = call.isAdmin && !call.meeting && inCall && shown.length < MAX_IN_CALL;
  // Из комнаты встречи сотрудников не зовут: круг допущенных задаёт событие,
  // а не звонок (в карточке встречи для этого есть «Позвать ещё»). Зато
  // ссылку для внешнего человека дают прямо здесь, из разговора (решение
  // Ильи 09.09.2026), поэтому кнопка открывает окно с одной ссылкой.
  const linkOnly = call.meeting && inCall && !!call.link;

  /* «Ещё» на телефоне: второстепенное — показ экрана, доска, участники,
     отсчёт, приглашение, раскладка и смена камеры. Главные четыре (микрофон,
     камера, реакции, переписка) остаются в ряду. */
  const moreItems: ScreenMenuItem[] = mobile
    ? [
        ...screenItems,
        ...boardItems,
        ...(contentActive
          ? [
              {
                label: call.stripShown ? 'Скрыть участников' : 'Показать участников',
                onPick: call.toggleStrip,
              },
            ]
          : []),
        ...(canSetTimer
          ? [
              {
                label: call.timerEndsAt ? 'Отсчёт: изменить или снять' : 'Завести отсчёт',
                onPick: () => setTimerOpen(true),
              },
            ]
          : []),
        ...(canInvite ? [{ label: 'Пригласить', onPick: () => setInvite(true) }] : []),
        ...(linkOnly ? [{ label: 'Ссылка для гостей', onPick: () => setInvite(true) }] : []),
        ...(call.camOn ? [{ label: 'Сменить камеру', onPick: () => void call.flipCamera() }] : []),
      ]
    : [];


  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void ref.current?.requestFullscreen().catch(() => {});
  };

  const camTip = call.camBlocked
    ? 'Камера запрещена для сайта — разрешите её в настройках браузера'
    : call.camOn
      ? 'Выключить камеру'
      : 'Включить камеру';

  const answerButtons = (
    <>
      <button className="call-b decline" onClick={call.decline} title="Отклонить" data-tip="Отклонить">
        <IconPhoneOff />
      </button>
      <button className="call-b accept" onClick={() => void call.accept()} title="Ответить" data-tip="Ответить">
        <IconPhone />
      </button>
    </>
  );

  return (
    <div
      ref={ref}
      className={`call${fresh && mobile ? ' enter' : ''}${viewer ? ' viewer' : ''}${
        // Отступ под панель нужен всегда, когда она открыта, а не только на
        // вкладке переписки: со списком участников сцена заезжала под панель
        // (замечание Ильи 15.09.2026).
        mobile ? (stripOn ? ' strip-bottom' : '') : sideOpen ? ' side-right' : ''
      }${boardShown && !board.collapsed ? ' draw-left' : ''}`}
    >
      <div className={`call-head${boardShown ? ' onlight' : ''}`}>
        {/* Возврат из крупного вида к мозаике (решение Ильи 16.09.2026):
            открывают его нажатием по плитке, и выход должен быть на виду. */}
        {mobile && !contentActive && layout === 'speaker' && (
          <button
            type="button"
            className="ibtn outline sm call-head-back"
            onClick={() => call.setLayout('grid')}
            title="Ко всем участникам"
            aria-label="Ко всем участникам"
          >
            <IconArrowLeft />
          </button>
        )}
        {/* на доске имя собеседника не нужно: смотрят на лист (решение Ильи 11.09.2026) */}
        {!boardShown && <b>{title}</b>}
        <i>
          {call.error || call.notice || status}
          {call.phase === 'active' && !call.error && !call.notice && (
            <span className="call-timers">
              <span className="call-timer" title="Прошло с начала разговора">
                <IconElapsed />
                {clock(call.seconds)}
              </span>
              {call.timerEndsAt && (
                <span
                  className={`call-timer${overtime ? ' over' : ''}`}
                  title={overtime ? 'Время вышло' : 'Осталось до конца'}
                >
                  <IconTimer />
                  {timerText(new Date(call.timerEndsAt), new Date())}
                </span>
              )}
            </span>
          )}
        </i>
        <div className="call-head-tools">
          {call.phase === 'active' && call.quality && (
            <span className={`call-q q${call.quality}`} title="Качество связи">
              <IconSignal level={call.quality} />
            </span>
          )}
          {/* «Сохранить в файл» — раздел 5.1 спеки доски: рядом с кнопками окна, слева от них,
              тем же размером (call-b mini); видна только ведущему при открытом листе. */}
          <BoardSaveButton board={board} />
          {/* полноэкранный режим браузера на телефоне не работает — кнопка на телефоне не нужна */}
          {windowButtons({ fullscreen, mini: call.view === 'mini', isGuest: call.isGuest })
            .filter((btn) => btn === 'mini' || !mobile)
            .map((btn) => (
              <button
                key={btn}
                className="call-b mini"
                onClick={btn === 'mini' ? call.minimize : toggleFullscreen}
                title={btn === 'mini' ? 'Свернуть в окно' : btn === 'expand' ? 'Во весь экран' : 'Обычный вид'} data-tip={btn === 'mini' ? 'Свернуть в окно' : btn === 'expand' ? 'Во весь экран' : 'Обычный вид'}
              >
                {btn === 'expand' ? <IconExpand /> : <IconMinimize />}
              </button>
            ))}
        </div>
      </div>

      <div className={`call-stage${contentActive ? ' content' : ''}`}>
        {/* Входящий: видно только, кто зовёт. Раскладка, полосы и кнопки
            появляются после ответа (решение Ильи 11.09.2026) — до ответа
            смотреть на пустые плитки незачем. */}
        {call.phase === 'incoming' ? (
          <div className="call-ring">
            <Avatar login={caller?.login ?? ''} name={caller?.name ?? 'Кто-то'} size={132} />
            <b>{caller?.name ?? 'Кто-то'}</b>
            <i>
              {activeCount > 1
                ? `зовёт в звонок · ${plural(activeCount, ['человек', 'человека', 'человек'])}`
                : call.kind === 'video'
                  ? 'Видеозвонок'
                  : 'Звонок'}
            </i>
            {/* На телефоне ответ и отказ стоят прямо под аватаром, а не внизу
                экрана (замечание Ильи 15.09.2026): рука держит телефон
                посередине, и тянуться к нижнему краю неудобно. */}
            {mobile && <div className="call-ring-acts">{answerButtons}</div>}
          </div>
        ) : call.phase === 'outgoing' ? (
          /* Дозвон — такая же карточка, как у входящего, но про того, кому
             звонят (решение Ильи 21.09.2026): раньше здесь стоял сам
             звонящий — своё лицо и своя фамилия вместо собеседника. В
             групповом дозвоне видно всех, кого зовём. Предпросмотр своей
             камеры не показываем (замечание Ильи 15.09.2026) — человек
             смотрит, ответят ему или нет, а не на себя. */
          <div className="call-ring">
            <div className="call-ring-people">
              {(others.length ? others : [null]).slice(0, RING_FACES).map((p, i) => (
                <Avatar
                  key={p?.id ?? i}
                  login={p?.login ?? ''}
                  name={p?.name ?? 'Кто-то'}
                  size={others.length > 1 ? 92 : 132}
                />
              ))}
              {others.length > RING_FACES && <span className="call-ring-more">+{others.length - RING_FACES}</span>}
            </div>
            <b>{callTitle(others.map((p) => p.name))}</b>
            <i>{call.kind === 'video' ? 'Видеозвонок · вызываем' : 'Звонок · вызываем'}</i>
          </div>
        ) : boardShown ? (
          <div className="call-screen">
            <BoardStage board={board} />
          </div>
        ) : sharer ? (
          /* Пометки поверх чужого кадра (решение Ильи 16.09.2026): рисуются
             тем же инструментом, что и доска, но нигде не хранятся и уходят
             вместе с показом. */
          <div className="call-screen">
            <ParticipantTile p={sharer} source="screen" fit="contain" hideName />
            <ScreenDrawLayer draw={screenDraw} color={board.color} widthIdx={board.widthIdx} />
          </div>
        ) : (
          <Grid
            people={duo ? others : shown}
            layout={layout}
            // вдвоём крупно всегда собеседник, даже когда говорю я: своё лицо
            // человек смотрит миниатюрой, а не во весь экран
            mainId={duo ? (others[0]?.id ?? null) : mainId}
            mobile={mobile}
            pinned={!!pinned && pinned === mainId}
            // Нажатие по плитке открывает крупный вид (решение Ильи
            // 16.09.2026). Закрепления оно не делает: в крупном виде всё так
            // же показывается говорящий, а выйти можно кнопкой «Назад».
            onPick={mobile ? () => call.setLayout('speaker') : undefined}
          />
        )}
        {/* В вебе правый край занимает одна панель с вкладками «Лица» и
            «Переписка» (решение Ильи 11.09.2026). На телефоне лица остаются
            полосой снизу, а переписка открывается экраном стопки. */}
        {!mobile && sideShown && (
          <CallSide
            leaving={sideLeaving}
            people={shown}
            tab={chatOpen ? 'chat' : 'faces'}
            onTab={(t) => setChatOpen(t === 'chat')}
            unread={call.unread}
            onCollapse={closeSide}
            onPick={(p, anchor) => setPicked({ id: p.id, name: p.name, isMe: p.isMe, anchor })}
          />
        )}
        {mobile && stripOn && (
          <Strip
            people={stripPeople}
            side={stripSide}
            // Нажатие по плитке открывает меню участника: закрепить может
            // каждый, удалить — только администратор (пункт добавляется ему).
            onPick={(p, anchor) => setPicked({ id: p.id, name: p.name, isMe: p.isMe, anchor })}
          />
        )}
        {cornerTile && (
          /* Миниатюра переносится по углам нажатием (решение Ильи
             11.09.2026): она закрывает то угол с лицом, то подпись. */
          <div
            className={`call-self c-${selfCorner}`}
            onClick={() => setSelfCorner((c) => NEXT_CORNER[c])}
            title="Перенести в другой угол"
          >
            <ParticipantTile p={cornerTile} size="sm" hideName={duo} />
          </div>
        )}
        {/* Свёрнутая панель оставляет язычок: без него список участников не вернуть. */}
        {!mobile && !sideOpen && call.phase === 'active' && (
          <button
            type="button"
            className="sb-collapse call-side-open"
            onClick={() => setPanelOpen(true)}
            title="Участники и переписка"
            data-tip="Участники и переписка"
            aria-label="Участники и переписка"
          >
            <IconCollapse />
          </button>
        )}
        {toast && (
          <div className="call-toast" role="status" onClick={() => setChatOpen(true)}>
            <b>{toast.name}</b>
            <span>{toast.text}</span>
          </div>
        )}
        {boardShown && <BoardPanel board={board} />}
        {/* Та же панель поверх демонстрации: цвета и толщины общие, инструмент
            в звонке один (решение Ильи 16.09.2026). */}
        {screenDraw.on && !boardShown && <BoardPanel board={board} />}
      </div>

      {call.phase === 'incoming' ? (
        // На телефоне те же кнопки уже стоят под аватаром — внизу их нет.
        mobile ? null : <div className="call-actions">{answerButtons}</div>
      ) : (
        <div className="call-actions">
          <div className="call-acts-row">
          <button
            className={`call-b${call.micOn ? '' : ' off'}`}
            onClick={call.toggleMic}
            title={call.micOn ? 'Выключить микрофон' : 'Включить микрофон'} data-tip={call.micOn ? 'Выключить микрофон' : 'Включить микрофон'}
          >
            {call.micOn ? <IconMic /> : <IconMicOff />}
          </button>
          {/* Камеру, запрещённую в настройках сайта, браузер второй раз не
              спрашивает: кнопка объясняет это подсказкой, а нажатие всё равно
              пробует снова — вдруг разрешение уже дали (замечание Ильи
              15.09.2026). */}
          <button
            className={`call-b${call.camOn ? '' : ' off'}`}
            onClick={() => void call.toggleCam()}
            title={camTip}
            data-tip={camTip}
          >
            {call.camOn ? <IconCamera /> : <IconCameraOff />}
          </button>
          {/* рука и реакции — одна кнопка: это один жанр, два места путают
              (решение Ильи 11.09.2026) */}
          <button
            className={`call-b${call.hand ? ' on' : ''}`}
            onClick={(e) => setReactionMenu(e.currentTarget)}
            title={call.hand ? 'Рука поднята' : 'Реакции'} data-tip={call.hand ? 'Рука поднята' : 'Реакции'}
            aria-label="Реакции"
          >
            <IconHand />
          </button>
          <button
            className={`call-b${chatOpen ? ' on' : ''}`}
            onClick={() => {
              setChatOpen((v) => !v);
              setPanelOpen(true);
            }}
            title="Переписка звонка" data-tip="Переписка звонка"
            aria-label="Переписка звонка"
          >
            <IconChat />
            {!chatOpen && call.unread > 0 && <i className="call-b-badge">{call.unread}</i>}
          </button>
          {!mobile && (screenItems.length > 0 || screenSupported) && (
            <button
              className={`call-b${call.screenOn ? ' on' : ''}`}
              onClick={(e) => setScreenMenu(e.currentTarget)}
              disabled={screenItems.length === 0}
              title={screenTitle} data-tip={screenTitle}
              aria-label={screenTitle}
            >
              <IconScreen />
            </button>
          )}
          {hostItems.length > 0 && (
            <button
              className="call-b"
              onClick={(e) => setHostMenu(e.currentTarget)}
              title="Передать роль ведущего"
              data-tip="Передать роль ведущего"
              aria-label="Передать роль ведущего"
            >
              <IconPresenter />
            </button>
          )}
          {!mobile && contentActive && (
            <button
              className={`call-b${call.stripShown ? ' on' : ''}`}
              onClick={call.toggleStrip}
              title={call.stripShown ? 'Скрыть участников' : 'Показать участников'} data-tip={call.stripShown ? 'Скрыть участников' : 'Показать участников'}
              aria-label="Участники"
            >
              <IconGroups />
            </button>
          )}
          {!mobile && canSetTimer && (
            <button
              className={`call-b${call.timerEndsAt ? ' on' : ''}`}
              onClick={() => setTimerOpen(true)}
              title={call.timerEndsAt ? 'Отсчёт: изменить или снять' : 'Завести отсчёт'} data-tip={call.timerEndsAt ? 'Отсчёт: изменить или снять' : 'Завести отсчёт'}
              aria-label="Отсчёт"
            >
              <IconTimer />
            </button>
          )}
          {/* Пометки поверх чужого или своего экрана: включаются кнопкой и
              стираются целиком — истории у них нет (решение Ильи 16.09.2026). */}
          {contentActive && !boardShown && (
            <button
              className={`call-b${screenDraw.on ? ' on' : ''}`}
              onClick={() => screenDraw.setOn(!screenDraw.on)}
              title={screenDraw.on ? 'Убрать перо' : 'Рисовать поверх экрана'}
              data-tip={screenDraw.on ? 'Убрать перо' : 'Рисовать поверх экрана'}
              aria-label="Рисовать поверх экрана"
            >
              <IconBoard />
            </button>
          )}
          {screenDraw.on && screenDraw.strokes.length > 0 && (
            <button
              className="call-b"
              onClick={screenDraw.clear}
              title="Удалить все пометки"
              data-tip="Удалить все пометки"
              aria-label="Удалить все пометки"
            >
              <IconEraser />
            </button>
          )}
          <BoardControl board={board} compact={mobile} onItems={setBoardItems} />
          {!mobile && canInvite && (
            <button className="call-b" onClick={() => setInvite(true)} title="Пригласить" data-tip="Пригласить" aria-label="Пригласить">
              <IconPlus />
            </button>
          )}
          {!mobile && linkOnly && (
            <button
              className="call-b"
              onClick={() => setInvite(true)}
              title="Ссылка для гостей" data-tip="Ссылка для гостей"
              aria-label="Ссылка для гостей"
            >
              <IconPlus />
            </button>
          )}
          {/* Раскладка переключается кнопкой в этой же плашке и на телефоне
              тоже (решение Ильи 11.09.2026): сегменту над сценой там не место.
              Вдвоём переключать нечего — сцена одна на двоих. */}
          {/* Телефон: в ряду только главные кнопки, остальное — списком с
              подписями под «ещё» (решение Ильи 11.09.2026). Значок на
              маленьком экране опознаётся хуже слова, а ряд от числа функций
              больше не зависит. */}
          {mobile && moreItems.length > 0 && (
            <button
              className="call-b"
              onClick={(e) => setMoreMenu(e.currentTarget)}
              title="Ещё"
              aria-label="Ещё"
            >
              <IconMore />
            </button>
          )}
          {/* Смена камеры на телефоне ушла в «ещё»: в ряду остаются только
              главные кнопки (решение Ильи 11.09.2026). */}
          {mobile
            ? null
            : (
                <button className="call-b" onClick={() => setMenu((m) => !m)} title="Настройки" data-tip="Настройки">
                  <IconGear />
                </button>
              )}
          </div>
          {/* завершить — всегда справа, отдельно от остальных: её ищут в спешке.
              На телефоне она вне прокручиваемого ряда и никуда не уезжает. */}
          <button
            className="call-b decline far"
            onClick={(e) => (call.isAdmin && !!call.link ? setEndMenu(e.currentTarget) : leave())}
            title="Завершить" data-tip="Завершить"
          >
            <IconPhoneOff />
          </button>
        </div>
      )}

      <div className="call-bars">
        {call.audioBlocked && (
          <div className="banner info" role="status">
            <b>Браузер не включил звук</b>
            <button type="button" className="btn outline sm" onClick={() => void call.resumeAudio()}>
              Включить
            </button>
          </div>
        )}
        {call.screenOn && (
          <div className="banner info" role="status">
            <b>Вы показываете экран</b>
            <button type="button" className="btn outline sm" onClick={call.stopScreen}>
              Остановить
            </button>
          </div>
        )}
        {board.progress && (
          <div className="banner info" role="status">
            <b>{board.progress}</b>
          </div>
        )}
        {board.error && (
          <div className="banner crit" role="alert">
            <b>{board.error}</b>
          </div>
        )}
      </div>

      {menu && <DeviceMenu onClose={() => setMenu(false)} />}
      {screenMenu && (
        <ScreenMenu anchor={screenMenu} items={screenItems} onClose={() => setScreenMenu(null)} />
      )}
      {moreMenu && (
        <ScreenMenu anchor={moreMenu} items={moreItems} onClose={() => setMoreMenu(null)} />
      )}
      {reactionMenu && (
        <ScreenMenu
          anchor={reactionMenu}
          items={reactionItems}
          onClose={() => setReactionMenu(null)}
        />
      )}
      {hostMenu && <ScreenMenu anchor={hostMenu} items={hostItems} onClose={() => setHostMenu(null)} />}
      {endMenu && <ScreenMenu anchor={endMenu} items={endItems} onClose={() => setEndMenu(null)} />}
      {mobile && chatOpen && (
        <div className="call-chat-stack">
          <PanelHead
            title="Переписка звонка"
            onBack={() => setChatOpen(false)}
            action={
              /* Таблетка возврата: видно, что звонок идёт, и куда вернуться.
                 Во время показа экрана и доски здесь живая миниатюра
                 содержимого (спека 7), пока — точка и отсчёт. */
              <button
                type="button"
                className="call-back-pill"
                onClick={() => setChatOpen(false)}
                title="Вернуться в звонок" data-tip="Вернуться в звонок"
              >
                {sharer ? (
                  <BackThumb id={sharer.id} />
                ) : boardShown ? (
                  <i className="call-back-board">
                    <IconBoard />
                  </i>
                ) : (
                  <i className="call-back-dot" />
                )}
                <span className="mono">{clock(call.seconds)}</span>
              </button>
            }
          />
          <CallChat />
        </div>
      )}
      {timerOpen && call.callId && (
        <TimerDialog callId={call.callId} on={!!call.timerEndsAt} onClose={() => setTimerOpen(false)} />
      )}
      {successor && (
        <InviteDialog
          title="Кому передать роль ведущего?"
          confirm="Передать и выйти"
          people={staffLeft.map((p) => ({
            id: p.id,
            name: p.name,
            login: '',
            avatarAt: p.avatarAt,
            lastSeen: null,
          }))}
          taken={new Set()}
          limit={1}
          onPick={(ids) => {
            setSuccessor(false);
            call.hangup(ids[0]);
          }}
          onClose={() => {
            // Закрыл окно, не выбрав: выходим всё равно — роль сервер отдаст
            // вошедшему раньше других (спека 3.1, «выбор можно пропустить»).
            setSuccessor(false);
            call.hangup();
          }}
        />
      )}
      {picked && (
        <ScreenMenu
          anchor={picked.anchor}
          items={[
            {
              label: pinned === picked.id ? 'Открепить' : `Закрепить — ${picked.name}`,
              onPick: () => setPinnedId(pinned === picked.id ? null : picked.id),
            },
            ...(call.isAdmin && !picked.isMe
              ? [
                  {
                    label: `Удалить из звонка — ${picked.name}`,
                    danger: true,
                    // сервер сам различает составной идентификатор гостя и
                    // идентификатор учётной записи сотрудника (спека 3.5)
                    onPick: () => void call.kick(picked.id),
                  },
                ]
              : []),
          ]}
          onClose={() => setPicked(null)}
        />
      )}
      {invite && (
        <InviteDialog
          title={linkOnly ? 'Ссылка для гостей' : 'Пригласить в звонок'}
          onlyLink={linkOnly}
          confirm="Пригласить"
          link={call.link}
          taken={new Set(shown.map((p) => p.id))}
          limit={Math.max(0, MAX_IN_CALL - shown.length)}
          onPick={(ids) => {
            setInvite(false);
            void call.invite(ids);
          }}
          onClose={() => setInvite(false)}
        />
      )}
    </div>
  );
}


/*
 * Живая миниатюра показа в шапке экрана переписки: пока идёт показ экрана,
 * человек видит, что там сменилось, не выходя из переписки (спека 7).
 */
function BackThumb({ id }: { id: string }) {
  const call = useCall();
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return call.attach(el, id, 'screen');
  }, [call, id]);
  return <video ref={ref} className="call-back-thumb" autoPlay playsInline muted aria-hidden />;
}
